import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { Row } from "@sprout/layout.tsx";
import JobSearchResultsColumn from "@components/job_search/results_column.tsx";
import JobDetailPanel from "@components/job_search/job_detail_panel.tsx";
import ApplyModal from "@components/job_search/apply_modal.tsx";
import { ApplicationSuccess } from "@components/job_post/application_success.tsx";
import DiscardProgressModal from "@components/job_post/discard_progress_modal.tsx";
import EmptyState from "@components/empty_state.tsx";
import Fingerprint1 from "@images/fingerprint_1.svg";
import useDevice from "@hooks/use_device.ts";
import useTranslation from "@hooks/use_translation.ts";
import useJobPostDetail from "@hooks/use_job_post_detail.ts";
import useSubscriptionModal from "@hooks/use_subscription_modal.ts";
import { newJobAlertPath } from "@/utils/new_job_alert_path.ts";
import type { JobRecommendation } from "@components/dashboard/job_recommendation_card.tsx";
import type { JobPostDetail } from "@/types/job_search/detail";
import type { JobPost, SearchParams } from "@/types/job_search/search";

export type Selection = { id: string; post: JobPost | null };

type Props = {
  jobPosts: JobPost[] | undefined;
  loading: boolean;
  browsing: boolean;
  moreResultsAvailable: boolean;
  currentPage: number;
  lastPublishDate: string | null;
  searchParams: SearchParams;
  createTrackSearchResultClick: (post: JobPost, position: number) => () => void;
  onView: (id: string) => void;
  onApplied: (jobPostId: string) => void;
  onApplySuccess: (detail: JobPostDetail) => Promise<void> | void;
  onPromote: (post: JobPost) => void;
  savedJobsEnabled: boolean;
  searchNonce: number;
  initialSelection?: Selection;
  initialDetail?: JobPostDetail;
  discardGuardRef?: RefObject<(action: () => void) => void>;
};

const recommendationToJobPost = (job: JobRecommendation): JobPost => ({
  id: String(job.id),
  title: job.title,
  companyName: job.companyName,
  logoUrl: job.logoUrl,
  publicUrl: job.publicUrl,
  urlToken: job.urlToken,
  departmentId: null,
  viewJobPath: job.viewJobPath,
  firstPublished: "",
  locations: job.locations,
  workType: job.workType,
  payRanges: job.payRanges ?? "",
  summary: job.summary,
  viewed: true,
  appliedAt: null,
});

export const JobSearchSplitView = ({
  jobPosts,
  loading,
  browsing,
  moreResultsAvailable,
  currentPage,
  lastPublishDate,
  searchParams,
  createTrackSearchResultClick,
  onView,
  onApplied,
  onApplySuccess,
  onPromote,
  savedJobsEnabled,
  searchNonce,
  initialSelection,
  initialDetail,
  discardGuardRef,
}: Props) => {
  const { t } = useTranslation("jobSearch");
  const device = useDevice();
  const { detail, status, select, clear } = useJobPostDetail(initialDetail);
  const { openModal } = useSubscriptionModal();
  const [selected, setSelected] = useState<Selection | null>(initialSelection ?? null);
  const [confirming, setConfirming] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const autoSelectedRef = useRef(!!initialSelection);
  const pendingActionsRef = useRef<(() => void)[]>([]);
  const dirtyRef = useRef<() => boolean>(() => false);
  const listRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const promotedIdRef = useRef<string | null>(null);
  const resultsQuery = useMemo(() => searchParams.query, [jobPosts]);

  const resetDirty = () => {
    dirtyRef.current = () => false;
  };

  const commitSelection = useCallback(
    (next: { post: JobPost } | null) => {
      resetDirty();
      setConfirming(false);
      setSelected(next && { id: next.post.id, post: next.post });

      if (!next) {
        clear();
        return;
      }

      select(next.post.viewJobPath ?? next.post.publicUrl);
    },
    [clear, select],
  );

  const confirmDiscard = useCallback(
    (action: () => void) => {
      if (dirtyRef.current() && !confirming) {
        pendingActionsRef.current.push(action);
        setShowDiscardConfirm(true);
        return;
      }

      action();
    },
    [confirming],
  );

  useEffect(() => {
    if (!discardGuardRef) return;

    discardGuardRef.current = confirmDiscard;
  }, [discardGuardRef, confirmDiscard]);

  const requestSelection = useCallback(
    (next: { post: JobPost } | null) => {
      if (next && next.post.id === selected?.id) return;

      confirmDiscard(() => commitSelection(next));
    },
    [commitSelection, confirmDiscard, selected?.id],
  );

  const handleSelect = useCallback(
    (post: JobPost) => requestSelection({ post }),
    [requestSelection],
  );

  useEffect(() => {
    if (searchNonce === 0) return;
    autoSelectedRef.current = false;
    pendingActionsRef.current = [];
    setShowDiscardConfirm(false);
    commitSelection(null);
  }, [searchNonce]);

  useEffect(() => {
    if (!device.isWeb || loading) return;
    if (selected || autoSelectedRef.current) return;
    if (!jobPosts?.length) return;

    const first = jobPosts[0];
    if (!first.viewJobPath) return;

    autoSelectedRef.current = true;
    if (!first.viewed) onView(first.id);
    commitSelection({ post: first });
  }, [device.isWeb, loading, jobPosts, selected, onView, commitSelection]);

  useEffect(() => {
    if (status !== "ready" || !detail || !selected?.post) return;
    if (selected.post.firstPublished || String(detail.jobPost.id) !== String(selected.id)) return;

    const enriched = {
      ...selected.post,
      firstPublished: detail.jobPost.first_published,
    };
    setSelected({ id: selected.id, post: enriched });
    onPromote(enriched);
  }, [status, detail]);

  const handleApplySuccess = async () => {
    if (!detail) return;
    await onApplySuccess(detail);
    onApplied(detail.jobPost.id);
    setConfirming(true);
  };

  const scrollToTop = (ref: RefObject<HTMLDivElement | null>) => {
    ref.current?.scrollTo({ top: 0 });
    if (!device.isWeb) {
      ref.current?.closest('[data-provides="scroll-container"]')?.scrollTo({ top: 0 });
    }
  };

  useEffect(() => {
    if (device.isMobile && selected) scrollToTop(panelRef);
  }, [selected?.id]);

  const handleCloseConfirmation = () => {
    resetDirty();
    setConfirming(false);
    setFormKey((key) => key + 1);
    scrollToTop(panelRef);
  };

  const handleSelectRecommendation = (job: JobRecommendation) => {
    const post =
      jobPosts?.find((existing) => String(existing.id) === String(job.id)) ??
      recommendationToJobPost(job);
    onPromote(post);
    commitSelection({ post });
    promotedIdRef.current = String(post.id);
  };

  useEffect(() => {
    if (!promotedIdRef.current || String(jobPosts?.[0]?.id) !== promotedIdRef.current) return;

    promotedIdRef.current = null;
    requestAnimationFrame(() => scrollToTop(listRef));
  }, [jobPosts]);

  if (jobPosts?.length === 0) {
    return (
      <div className="w-full max-w-[1550px] mx-auto px-6 pb-8">
        <div className="bg-white rounded-lg">
          <EmptyState
            svg={Fingerprint1}
            title={
              resultsQuery
                ? t("noResultsForSearchTerm", { searchTerm: resultsQuery })
                : t("noResults")
            }
            message={resultsQuery ? t("noResultsForSearchTermMessage") : t("noResultsMessage")}
          />
        </div>
      </div>
    );
  }

  const alertPost =
    selected?.post ?? jobPosts?.find((post) => String(post.id) === String(selected?.id));
  const handleCreateAlert =
    savedJobsEnabled && alertPost ? () => openModal(newJobAlertPath(alertPost)) : undefined;

  const showList = device.isWeb || !selected;
  const showDetail = device.isWeb || !!selected;

  return (
    <>
      <Row gap="24px" className="h-full w-full max-w-[1550px] mx-auto px-6 pb-8">
        {showList && (
          <JobSearchResultsColumn
            jobPosts={jobPosts}
            browsing={browsing}
            moreResultsAvailable={moreResultsAvailable}
            currentPage={currentPage}
            lastPublishDate={lastPublishDate}
            searchParams={searchParams}
            createTrackSearchResultClick={createTrackSearchResultClick}
            onView={onView}
            onSelect={handleSelect}
            selectedId={selected?.id ?? null}
            savedJobsEnabled={savedJobsEnabled}
            scrollRef={listRef}
          />
        )}
        {showDetail && (
          <JobDetailPanel
            selectedPost={selected?.post ?? null}
            detail={detail}
            status={status}
            formKey={formKey}
            onSuccess={handleApplySuccess}
            onBack={() => requestSelection(null)}
            dirtyRef={dirtyRef}
            scrollRef={panelRef}
            onCreateAlert={handleCreateAlert}
          />
        )}
      </Row>
      {detail && (
        <ApplyModal open={confirming} narrow onClose={handleCloseConfirmation}>
          <ApplicationSuccess
            jobPost={detail.jobPost}
            onViewJobs={handleCloseConfirmation}
            onSelectRecommendation={handleSelectRecommendation}
          />
        </ApplyModal>
      )}
      <DiscardProgressModal
        open={showDiscardConfirm}
        onOpenChange={(open) => {
          setShowDiscardConfirm(open);
          if (!open) pendingActionsRef.current = [];
        }}
        onDiscard={() => {
          const actions = pendingActionsRef.current;
          pendingActionsRef.current = [];
          setShowDiscardConfirm(false);
          actions.forEach((action) => action());
        }}
      />
    </>
  );
};

export default JobSearchSplitView;
